import "./incident.page.css";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { DatePicker, Drawer, Input, Select, Spin, Table, Tabs, Tooltip } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose, faFilter, faSearch } from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";
import { Navbar } from "@src/components/navbar";
import {
    getCustomerIncidentAlertsQuery, getCustomerIncidentDashboardQuery,
    getCustomerSiteIncidentsQuery, markCustomerIncidentsSeenMutation,
} from "@src/util/query/incident.query";
import { INCIDENT_STATUSES, INCIDENT_TYPES, IncidentStatusTag, formatIncidentWhen } from "./incidentMeta";

const { RangePicker } = DatePicker;

// Incident grid - same shape as wfc-public's Incident Reporting grid: "Title - N
// records" header, view tab, search + "Filters" bar, and the "All Site Incidents"
// columns (Site, Site Address, Employee, Reported At, Status, Incident Date,
// Incident Time). Plus a Dashboard tab.

const RANGE_PRESETS = [
    { label: "Last 30 days", value: [dayjs().subtract(29, "day"), dayjs()] },
    { label: "Last 90 days", value: [dayjs().subtract(89, "day"), dayjs()] },
    { label: "Last 12 months", value: [dayjs().subtract(1, "year").add(1, "day"), dayjs()] },
];

// Column chart, one hue. Only the axis carries numbers - exact counts are on hover.
const TrendChart = ({ trend }) => {
    const points = trend?.points || [];
    if (!points.length) return <div className="inc-chart-empty">No incidents in this period</div>;

    const max = Math.max(1, ...points.map((p) => p.count));
    // counted back from the latest bucket, so the most recent one is always labelled
    const labelEvery = Math.max(1, Math.ceil(points.length / 7));
    const daily = trend?.granularity === "day";

    return (
        <div className="inc-trend">
            <div className="inc-trend-plot">
                <div className="inc-trend-gridline inc-trend-gridline-top"><span>{max}</span></div>
                <div className="inc-trend-gridline inc-trend-gridline-mid"><span>{max > 1 ? Math.round(max / 2) : ""}</span></div>
                {points.map((p) => (
                    <Tooltip
                        key={p.key}
                        title={`${p.count} incident${p.count === 1 ? "" : "s"} · ${daily ? dayjs(p.key).format("ddd DD/MM/YYYY") : dayjs(p.key + "-01").format("MMMM YYYY")}`}
                        mouseEnterDelay={0}
                    >
                        <div className="inc-trend-col">
                            {p.count > 0 && <div className="inc-trend-bar" style={{ height: `${Math.max((p.count / max) * 100, 3)}%` }} />}
                        </div>
                    </Tooltip>
                ))}
            </div>
            <div className="inc-trend-axis">
                {points.map((p, i) => (
                    <div key={p.key} className="inc-trend-tick">
                        {(points.length - 1 - i) % labelEvery === 0 && <span>{p.label}</span>}
                    </div>
                ))}
            </div>
        </div>
    );
};

// Horizontal bars, one hue, length relative to the largest row. Each row is a
// button that opens the grid filtered to it.
const BarList = ({ rows, total, onSelect, emptyText }) => {
    if (!rows.length) return <div className="inc-chart-empty">{emptyText}</div>;
    const max = Math.max(1, ...rows.map((r) => r.count));

    return (
        <div className="inc-barlist">
            {rows.map((r) => {
                const pct = total ? Math.round((r.count / total) * 100) : 0;
                return (
                    <Tooltip key={r.key} title={`${r.count} incident${r.count === 1 ? "" : "s"} · ${pct}% of total · click to view`} placement="topLeft" mouseEnterDelay={0.1}>
                        <button type="button" className="inc-bar-row" onClick={() => onSelect?.(r.key)}>
                            <span className="inc-bar-label">{r.label}</span>
                            <span className="inc-bar-track">
                                <span className="inc-bar-fill" style={{ width: `${Math.max((r.count / max) * 100, 2)}%` }} />
                            </span>
                            <span className="inc-bar-value">{r.count}</span>
                        </button>
                    </Tooltip>
                );
            })}
        </div>
    );
};

const Stat = ({ value, label, sub, loading, onClick }) => (
    <div className={`inc-stat${onClick ? " inc-stat-clickable" : ""}`} onClick={onClick} role={onClick ? "button" : undefined}>
        <div className="inc-stat-value">{loading ? <Spin size="small" /> : value}</div>
        <div className="inc-stat-label">{label}</div>
        {sub && !loading && <div className="inc-stat-sub">{sub}</div>}
    </div>
);

const Section = ({ title, children }) => (
    <div className="hr-section inc-section">
        <div className="hr-info-title">{title}</div>
        {children}
    </div>
);

export const Incidents = () => {
    const navigate = useNavigate();
    const [customerDetail, setCustomerDetail] = useState(null);

    useEffect(() => {
        const detail = localStorage.getItem("customerDetail");
        if (detail) setCustomerDetail(JSON.parse(detail));
    }, []);

    const [tab, setTab] = useState("list");
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

    // ── filters (drawer) - site + date also scope the Dashboard tab ──
    const [range, setRange] = useState(null);
    const [siteId, setSiteId] = useState(undefined);
    const [status, setStatus] = useState(undefined);
    const [incidentType, setIncidentType] = useState(undefined);

    // ── grid search ──
    const [searchText, setSearchText] = useState("");
    const [search, setSearch] = useState("");

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [sortOrder, setSortOrder] = useState("desc");

    useEffect(() => {
        const t = setTimeout(() => setSearch(searchText.trim()), 300);
        return () => clearTimeout(t);
    }, [searchText]);

    useEffect(() => { setPage(1); }, [range, siteId, status, incidentType, search]);

    const scope = {
        siteId: siteId || null,
        startDate: range?.[0]?.format("YYYY-MM-DD") || null,
        endDate: range?.[1]?.format("YYYY-MM-DD") || null,
    };

    const { data: dashData, loading: dashLoading } = useQuery(getCustomerIncidentDashboardQuery, {
        variables: { input: scope },
        fetchPolicy: "cache-and-network",
    });

    const { data: listData, loading: listLoading } = useQuery(getCustomerSiteIncidentsQuery, {
        variables: { input: { ...scope, status: status || null, incidentType: incidentType || null, search: search || null, page, pageSize, sortOrder } },
        fetchPolicy: "cache-and-network",
    });

    // Opening this page counts as having seen every incident so far (clears the
    // navbar bell). The previous seenAt is kept first, so incidents that arrived
    // since the last visit still carry an "Unread" tag in the grid for this visit.
    const { data: alertsData } = useQuery(getCustomerIncidentAlertsQuery, { fetchPolicy: "network-only" });
    const [markSeen] = useMutation(markCustomerIncidentsSeenMutation, {
        refetchQueries: [{ query: getCustomerIncidentAlertsQuery }],
    });
    const [newSince, setNewSince] = useState(null);
    const markedRef = useRef(false);
    useEffect(() => {
        const alerts = alertsData?.getCustomerIncidentAlerts?.response;
        if (!alerts || markedRef.current) return;
        markedRef.current = true;
        setNewSince(alerts.seenAt);
        markSeen().catch(() => {});
    }, [alertsData, markSeen]);

    const dash = dashData?.getCustomerIncidentDashboard?.response;
    const rows = listData?.getCustomerSiteIncidents?.response || [];
    const totalCount = Number(listData?.getCustomerSiteIncidents?.totalCount || 0);
    const sites = dash?.sites || [];
    const total = dash?.total || 0;
    const statsLoading = dashLoading && !dash;

    const activeFilterCount = [range, siteId, status, incidentType].filter(Boolean).length;
    const clearAllFilters = () => { setRange(null); setSiteId(undefined); setStatus(undefined); setIncidentType(undefined); };

    // dashboard -> grid, filtered to whatever was clicked
    const showList = (patch) => {
        setSearchText(""); setStatus(patch.status); setIncidentType(patch.incidentType);
        if (patch.siteId) setSiteId(patch.siteId);
        setTab("list");
    };

    const statusRows = Object.entries(INCIDENT_STATUSES)
        .map(([key, s]) => ({ key, label: s.label, count: dash?.byStatus?.find((b) => b.status === key)?.count || 0 }))
        .filter((r) => r.count > 0);
    const typeRows = (dash?.byType || []).map((t) => ({ key: t.type, label: t.type, count: t.count }));
    const siteRows = (dash?.bySite || []).map((s) => ({ key: String(s.siteId), label: s.site, count: s.count }));
    const typeOptions = [...new Set([...INCIDENT_TYPES, ...typeRows.map((t) => t.key)])].map((t) => ({ label: t, value: t }));

    const pct = (n) => (total ? `${Math.round((n / total) * 100)}% of all incidents` : null);

    // same columns, same order as wfc-public's "All Site Incidents" view
    const columns = [
        {
            title: "Site",
            dataIndex: "site",
            key: "site",
            render: (site, r) => (
                <span className="inc-incident-cell">
                    <span className="inc-link">{site || "—"}</span>
                    {newSince && dayjs(r.reportedAt).isAfter(newSince) && <span className="inc-unread-tag">Unread</span>}
                </span>
            ),
        },
        {
            title: "Site Address",
            dataIndex: "siteAddress",
            key: "siteAddress",
            responsive: ["lg"],
            render: (v) => v || "—",
        },
        {
            title: "Employee",
            dataIndex: "reportedBy",
            key: "reportedBy",
            responsive: ["md"],
            render: (v) => v || "—",
        },
        {
            title: "Reported At",
            dataIndex: "reportedAt",
            key: "reportedAt",
            responsive: ["md"],
            sorter: true,
            sortOrder: sortOrder === "asc" ? "ascend" : "descend",
            sortDirections: ["ascend", "descend", "ascend"],
            render: (d) => <span className="inc-nowrap">{dayjs(d).format("DD/MM/YYYY")}</span>,
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (s) => <IncidentStatusTag status={s} />,
        },
        {
            title: "Incident Date",
            key: "incidentDate",
            responsive: ["sm"],
            render: (_, r) => <span className="inc-nowrap">{formatIncidentWhen(r.incidentDate)}</span>,
        },
        {
            title: "Incident Time",
            dataIndex: "incidentTime",
            key: "incidentTime",
            responsive: ["lg"],
            render: (v) => v || "—",
        },
    ];

    return (
        <div className="inc-page">
            <Navbar customerDetail={customerDetail} />

            {/* same header shape as wfc-public's GridHeader: "Title - N records" */}
            <div className="inc-grid-head">
                <div className="inc-grid-title">
                    Site Incidents
                    <span className="inc-grid-subtitle"> - {tab === "list" ? totalCount : total} records</span>
                </div>
            </div>

            <div className="inc-content">
                <div className="setting-body-inner grid-tabs inc-tabs">
                    <Tabs
                        activeKey={tab}
                        onChange={setTab}
                        items={[
                            { key: "list", label: "All Site Incidents" },
                            // { key: "dashboard", label: "Dashboard" },
                        ]}
                    />
                </div>

                {/* same bar as wfc-public's GridFilter: search + "Filters (N)" */}
                <div className="inc-filters">
                    {tab === "list" && (
                        <Input
                            type="search"
                            allowClear
                            className="generic-input-control inc-search"
                            placeholder="Search Site Incidents..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            suffix={<FontAwesomeIcon style={{ color: "#0091ae" }} icon={faSearch} />}
                        />
                    )}
                    <span className="grid-text-btn" onClick={() => setFilterDrawerOpen(true)}>
                        <FontAwesomeIcon color="#0091ae" icon={faFilter} />
                        &nbsp;Filters ({activeFilterCount})
                    </span>
                    {activeFilterCount > 0 && <span className="grid-text-btn" onClick={clearAllFilters}>Clear all</span>}
                </div>

                <Drawer
                    title="Filters"
                    placement="right"
                    className="portal-filter-drawer"
                    closable={true}
                    onClose={() => setFilterDrawerOpen(false)}
                    closeIcon={<FontAwesomeIcon icon={faClose} className="close-icon" />}
                    open={filterDrawerOpen}
                    width={500}
                    footer={
                        <div className="drawer-footer">
                            <button className="drawer-outlined-btn" onClick={clearAllFilters}>Clear all</button>
                            <button className="drawer-filled-btn" onClick={() => setFilterDrawerOpen(false)}>Done</button>
                        </div>
                    }
                >
                    <div className="filter-section">
                        <div>Reported between</div>
                        <RangePicker
                            className="custom-range-picker"
                            style={{ width: "100%" }}
                            format="DD/MM/YYYY"
                            placeholder={["All time", "Today"]}
                            presets={RANGE_PRESETS}
                            value={range}
                            onChange={(dates) => setRange(dates?.[0] && dates?.[1] ? dates : null)}
                        />
                    </div>
                    <div className="filter-section">
                        <div>Site</div>
                        <Select
                            className="custom-select"
                            style={{ width: "100%" }}
                            placeholder="All sites"
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            value={siteId}
                            onChange={setSiteId}
                            options={sites.map((s) => ({ label: s.sitename, value: String(s._id) }))}
                        />
                    </div>
                    <div className="filter-section">
                        <div>Status</div>
                        <Select
                            className="custom-select"
                            style={{ width: "100%" }}
                            placeholder="All statuses"
                            allowClear
                            value={status}
                            onChange={setStatus}
                            options={[
                                { label: "Open (not resolved)", value: "open" },
                                ...Object.entries(INCIDENT_STATUSES).map(([value, s]) => ({ label: s.label, value })),
                            ]}
                        />
                    </div>
                    <div className="filter-section">
                        <div>Incident type</div>
                        <Select
                            className="custom-select"
                            style={{ width: "100%" }}
                            placeholder="All types"
                            allowClear
                            value={incidentType}
                            onChange={setIncidentType}
                            options={typeOptions}
                        />
                    </div>
                </Drawer>

                {tab === "list" ? (
                    <Table
                        className="portal-table inc-table"
                        rowKey="_id"
                        columns={columns}
                        dataSource={rows}
                        loading={listLoading && !listData}
                        bordered={false}
                        onRow={(r) => ({ onClick: () => navigate(`/customer/incidents/${r._id}`) })}
                        onChange={(pagination, _filters, sorter) => {
                            if (pagination.pageSize !== pageSize) { setPageSize(pagination.pageSize); setPage(1); }
                            else setPage(pagination.current);
                            if (sorter?.order) setSortOrder(sorter.order === "ascend" ? "asc" : "desc");
                        }}
                        locale={{
                            emptyText: total === 0 && !search && activeFilterCount === 0
                                ? "No incidents have been reported on your sites"
                                : "No incidents match these filters",
                        }}
                        pagination={{
                            current: page,
                            pageSize,
                            total: totalCount,
                            showSizeChanger: true,
                            pageSizeOptions: ["20", "50", "100"],
                            showTotal: (t) => `${t} incident${t === 1 ? "" : "s"}`,
                        }}
                    />
                ) : (
                    <div className="inc-dashboard">
                        <div className="inc-stats">
                            <Stat value={total} label="Total incidents" loading={statsLoading} onClick={() => showList({})} />
                            <Stat value={dash?.open ?? 0} label="Open" sub={pct(dash?.open || 0)} loading={statsLoading} onClick={() => showList({ status: "open" })} />
                            <Stat value={dash?.closed ?? 0} label="Resolved or closed" sub={pct(dash?.closed || 0)} loading={statsLoading} />
                            <Stat value={dash?.policeAttended ?? 0} label="Police attended" sub={pct(dash?.policeAttended || 0)} loading={statsLoading} />
                        </div>

                        <div className="inc-row inc-row-wide">
                            <Section title="Incidents Over Time">
                                {statsLoading ? <div className="inc-loading"><Spin /></div> : <TrendChart trend={dash?.trend} />}
                            </Section>
                            <Section title="By Status">
                                {statsLoading ? <div className="inc-loading"><Spin /></div> : (
                                    <BarList rows={statusRows} total={total} onSelect={(key) => showList({ status: key })} emptyText="No incidents yet" />
                                )}
                            </Section>
                        </div>

                        <div className={`inc-row${siteRows.length > 1 ? " inc-row-halves" : ""}`}>
                            <Section title="By Incident Type">
                                {statsLoading ? <div className="inc-loading"><Spin /></div> : (
                                    <BarList rows={typeRows} total={total} onSelect={(key) => showList({ incidentType: key })} emptyText="No incidents yet" />
                                )}
                            </Section>
                            {siteRows.length > 1 && (
                                <Section title="Sites With Most Incidents">
                                    <BarList rows={siteRows} total={total} onSelect={(key) => showList({ siteId: key })} emptyText="No incidents yet" />
                                </Section>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
